import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Logo from "./Logo";
import { ArrowUpRight, Mail } from "lucide-react";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSubmitting(true);
        try {
            await api.post("/newsletter", { email: email.trim() });
            toast.success("You're on the list — see you Mondays.");
            setEmail("");
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="bg-zinc-50 text-zinc-700 border-t border-zinc-200">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <Logo variant="light" />
                        <p className="mt-5 text-sm text-zinc-600 max-w-sm leading-relaxed">
                            The real-time sales qualification assistant. Score deals live, ask sharper
                            questions, coach smarter — and close more.
                        </p>
                        <form onSubmit={handleSubscribe} data-testid="footer-newsletter-form" className="mt-6 flex max-w-sm items-center gap-2">
                            <div className="relative flex-1">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    data-testid="footer-newsletter-email"
                                    className="w-full h-10 rounded-lg bg-white border border-zinc-300 pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="footer-newsletter-submit"
                                className="h-10 px-4 rounded-lg bg-brand-primary hover:bg-brand-hover text-white text-sm font-medium disabled:opacity-60"
                            >
                                {submitting ? "..." : "Subscribe"}
                            </button>
                        </form>
                        <p className="mt-3 text-xs text-zinc-500">Monday digest. No spam. Unsubscribe anytime.</p>
                    </div>

                    <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4">Product</h4>
                            <ul className="space-y-3 text-sm">
                                <li><Link to="/features" className="hover:text-zinc-900">Features</Link></li>
                                <li><Link to="/pricing" className="hover:text-zinc-900">Pricing</Link></li>
                                <li><Link to="/#roi-calculator" className="hover:text-zinc-900">ROI Calculator</Link></li>
                                <li><Link to="/#faq" className="hover:text-zinc-900">FAQ</Link></li>
                                <li><Link to="/contact" className="hover:text-zinc-900 inline-flex items-center gap-1">Get Early Access <ArrowUpRight className="h-3 w-3" /></Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4">Company</h4>
                            <ul className="space-y-3 text-sm">
                                <li><Link to="/blog" data-testid="footer-blog" className="hover:text-zinc-900">Blog</Link></li>
                                <li><Link to="/contact" data-testid="footer-contact" className="hover:text-zinc-900">Contact</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4">Legal</h4>
                            <ul className="space-y-3 text-sm">
                                <li><Link to="/terms" className="hover:text-zinc-900">Terms of Service</Link></li>
                                <li><Link to="/privacy" className="hover:text-zinc-900">Privacy Policy</Link></li>
                                <li><Link to="/refund-policy" data-testid="footer-refund" className="hover:text-zinc-900">Cancellation & Refund</Link></li>
                                <li><a href="https://app.pipeguide.tech/data-processing" target="_blank" rel="noopener noreferrer" data-testid="footer-data-processing" className="hover:text-zinc-900">Data Processing</a></li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="mt-14 pt-8 border-t border-zinc-200 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <p className="text-xs text-zinc-500">© {new Date().getFullYear()} PipeGuide. All rights reserved.</p>
                    <div className="flex items-center gap-4">
                        <a href="mailto:support@pipeguide.tech" className="text-xs text-zinc-500 hover:text-zinc-700">support@pipeguide.tech</a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
